/**
 * Auth session helpers — read the persisted login data from local cache.
 *
 * After a successful login, the auth store (zustand + persist) writes:
 *   • `localStorage["auth-store"]`  → JSON { state: { user, rawUser, token, ... } }
 *   • `localStorage["auth_token"]`  → raw token string (for axios bootstrap)
 *
 * Use these helpers from anywhere (React components, non-React modules,
 * service workers) to read the cached identity without going through the
 * network. For reactive UI prefer `useAuthSession()`.
 */

import { useAuthStore, AUTH_STORAGE_KEY, AUTH_TOKEN_STORAGE_KEY } from '@/store/authStore';
import type { AuthUser, RawAuthUser } from '@/store/authStore';

export interface AuthSession {
  user: AuthUser | null;
  rawUser: RawAuthUser | null;
  token: string | null;
  loggedInAt: number | null;
  isAuthenticated: boolean;
}

interface PersistedShape {
  state?: Partial<AuthSession>;
}

function readFromLocalStorage(): AuthSession {
  const empty: AuthSession = {
    user: null,
    rawUser: null,
    token: null,
    loggedInAt: null,
    isAuthenticated: false,
  };

  if (typeof window === 'undefined') return empty;

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      const token = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      return token ? { ...empty, token, isAuthenticated: true } : empty;
    }
    const parsed = JSON.parse(raw) as PersistedShape;
    const state = parsed.state ?? {};
    return {
      user: (state.user as AuthUser | null) ?? null,
      rawUser: (state.rawUser as RawAuthUser | null) ?? null,
      token: (state.token as string | null) ?? null,
      loggedInAt: (state.loggedInAt as number | null) ?? null,
      isAuthenticated: Boolean(state.isAuthenticated),
    };
  } catch {
    return empty;
  }
}

/**
 * Get the full cached auth session (user + raw payload + token).
 * Reads from the in-memory zustand store first; falls back to localStorage.
 *
 * @example
 *   const { user, token, rawUser } = getAuthSession();
 *   if (!user) navigate('/login');
 */
export function getAuthSession(): AuthSession {
  const s = useAuthStore.getState();
  if (s.user || s.token) {
    return {
      user: s.user,
      rawUser: s.rawUser,
      token: s.token,
      loggedInAt: s.loggedInAt,
      isAuthenticated: s.isAuthenticated,
    };
  }
  return readFromLocalStorage();
}

/** Returns the normalized user object or `null` if not logged in. */
export function getCurrentUser(): AuthUser | null {
  return getAuthSession().user;
}

/** Returns the unmodified user object from the backend (extra fields). */
export function getRawUser(): RawAuthUser | null {
  return getAuthSession().rawUser;
}

/** Returns just the user id (or `null`). */
export function getCurrentUserId(): string | null {
  return getAuthSession().user?.id ?? null;
}

/** Returns the persisted JWT / auth token (or `null`). */
export function getAuthToken(): string | null {
  return getAuthSession().token;
}

/** Quick boolean check used by guards / routing. */
export function isAuthenticated(): boolean {
  return getAuthSession().isAuthenticated;
}

/**
 * Read an arbitrary field from the raw user payload.
 *
 * @example
 *   const schoolId = getCachedUserField<string>('schoolId');
 *   const phone    = getCachedUserField<string>('mobileNumber');
 */
export function getCachedUserField<T = unknown>(key: string): T | undefined {
  const raw = getRawUser();
  return raw ? (raw[key] as T | undefined) : undefined;
}

// ---------------------------------------------------------------------------
// React hook — subscribe to session changes inside components.
// ---------------------------------------------------------------------------

/**
 * React hook returning the cached session and re-rendering when it changes.
 *
 * @example
 *   const { user, isAuthenticated } = useAuthSession();
 *   return <p>Hello {user?.name}</p>;
 */
export function useAuthSession(): AuthSession {
  return useAuthStore((s) => ({
    user: s.user,
    rawUser: s.rawUser,
    token: s.token,
    loggedInAt: s.loggedInAt,
    isAuthenticated: s.isAuthenticated,
  }));
}

/** Convenience hook returning just the user. */
export function useCurrentUser(): AuthUser | null {
  return useAuthStore((s) => s.user);
}
